/**
 * Normalizes an API base URL so endpoint paths can be appended directly. The runtime
 * environment config may or may not end its `BaseURL` with a slash.
 */
export const withTrailingSlash = (baseUrl: string): string =>
  baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;

/**
 * Judge Review endpoints (`judge-review`).
 *
 * `{filingId}` is the reviewed filing's `ReviewedFilingUniqueId` (GUID), which is also the
 * working-store key. `{documentStoreId}` is the working-copy DocStore GUID of a document in
 * the in-progress action (`currentAction.documents[].judgeReviewDocumentStoreId`).
 */
export interface IJudgeReviewEndpoints {
  /** GET: the reviewed filings pending the judge. */
  summaries: () => string;
  /**
   * GET: the working-store filing (created on first open). POST: reset the in-progress action.
   * PATCH: JSON Patch (edits live under `/currentAction/...`). DELETE: discard the working store.
   */
  filing: (filingId: string) => string;
  /** GET: filer / authorizer, fees, upload limits and per-document configuration. */
  configuration: (filingId: string) => string;
  /**
   * PUT: set the documents of the in-progress action (copy-on-open creates each document's
   * working copy and returns its `judgeReviewDocumentStoreId`).
   */
  currentActionDocuments: (filingId: string) => string;
  /** POST: commit uploaded documents (`?finalize=false|true`); body is the document store ids. */
  documentUpload: (filingId: string) => string;
  /** GET: the judge banner. */
  banner: (filingId: string) => string;
  /** DELETE: reset one document's rendering, keyed by the integer `existingDocumentId`. */
  documentRendering: (filingId: string, existingDocumentId: number) => string;
  /** DELETE: delete one named rendering, keyed by the DocStore GUID. */
  singleRendering: (
    filingId: string,
    documentStoreId: string,
    renderingName: string,
  ) => string;
  /** GET: stream a working rendering of a document for the viewer. */
  renderingDownload: (
    filingId: string,
    documentStoreId: string,
    rendering: string,
  ) => string;
  /** POST: append a blank page (raw PDF bytes). */
  blankPage: (filingId: string, documentStoreId: string) => string;
  /** POST: submit the in-progress action. */
  submit: (filingId: string) => string;
  /** POST: mark a document reviewed (legacy stepper; superseded by document `status`). */
  documentReviewed: (filingId: string, existingDocumentId: number) => string;
}

/**
 * Judge Review endpoints
 * @param versionedUrl The API base URL, ending in a slash.
 */
export const judgeReviewEndpoints = (
  versionedUrl: string,
): IJudgeReviewEndpoints => {
  const baseUrl = `${versionedUrl}judge-review`;
  const filingUrl = (filingId: string) => `${baseUrl}/filing/${filingId}`;

  return {
    summaries: () => `${baseUrl}/summaries`,
    filing: filingUrl,
    configuration: (filingId: string) => `${filingUrl(filingId)}/configuration`,
    currentActionDocuments: (filingId: string) =>
      `${filingUrl(filingId)}/current-action/documents`,
    documentUpload: (filingId: string) =>
      `${filingUrl(filingId)}/document-upload`,
    banner: (filingId: string) => `${filingUrl(filingId)}/banner`,
    documentRendering: (filingId: string, existingDocumentId: number) =>
      `${filingUrl(filingId)}/document/${existingDocumentId}/rendering`,
    singleRendering: (
      filingId: string,
      documentStoreId: string,
      renderingName: string,
    ) =>
      `${filingUrl(filingId)}/document/${documentStoreId}/rendering/${renderingName}`,
    renderingDownload: (
      filingId: string,
      documentStoreId: string,
      rendering: string,
    ) =>
      `${filingUrl(filingId)}/document/${documentStoreId}/rendering/${rendering}/download`,
    blankPage: (filingId: string, documentStoreId: string) =>
      `${filingUrl(filingId)}/document/${documentStoreId}/blank-page`,
    submit: (filingId: string) => `${filingUrl(filingId)}/submit`,
    documentReviewed: (filingId: string, existingDocumentId: number) =>
      `${filingUrl(filingId)}/document/${existingDocumentId}/reviewed`,
  };
};

/**
 * Judge Chambers endpoints (`judge-chambers`): chamber tags, notes, assignments,
 * notifications and saved filters, plus the clerk-review notes surface.
 */
export interface IJudgeChambersEndpoints {
  /** GET: the user's chambers with tags and unread notification count. */
  summary: () => string;
  /** POST: create a chamber tag. */
  tags: (chamberId: string) => string;
  /** PUT: rename. DELETE: delete. */
  tag: (chamberId: string, tagId: string) => string;
  /** POST: apply a tag to a document. DELETE: remove it. */
  documentTag: (chamberId: string, documentId: number, tagId: string) => string;
  /** GET: the tags on a filing's documents. */
  filingDocumentTags: (filingUniqueId: string) => string;
  /** GET / POST: a document's notes. */
  documentNotes: (chamberId: string, documentId: number) => string;
  /** GET / POST: a case's notes. */
  caseNotes: (chamberId: string, caseId: number) => string;
  /** PUT / DELETE: one note. */
  note: (chamberId: string, noteId: string) => string;
  /** GET: the notes on a filing. */
  filingNotes: (filingUniqueId: string) => string;
  /** POST: reply to a note. */
  noteReplies: (chamberId: string, noteId: string) => string;
  /** GET: users that can be @-mentioned in a chamber. */
  mentionCandidates: (chamberId: string) => string;
  /** GET: users that can be @-mentioned on a Clerk Review document. */
  clerkReviewMentionCandidates: (documentId: number) => string;
  /** POST: query the chamber notes on Clerk Review documents. */
  clerkReviewDocumentNotesQuery: () => string;
  /** GET / POST: notes on a Clerk Review document. */
  clerkReviewDocumentNotes: (documentId: number) => string;
  /** PUT / DELETE: one Clerk Review note. */
  clerkReviewNote: (noteId: string) => string;
  /** POST: reply to a Clerk Review note. */
  clerkReviewNoteReplies: (noteId: string) => string;
  /** PUT: assign or unassign a document. */
  documentAssignee: (chamberId: string, documentId: number) => string;
  /** GET: assignments on a filing's documents. */
  filingAssignments: (filingUniqueId: string) => string;
  /** GET / DELETE-all: the user's notifications. */
  notifications: () => string;
  /** PUT: mark one notification read. */
  notificationRead: (notificationId: string) => string;
  /** PUT: mark notifications read. */
  notificationsRead: () => string;
  /** DELETE: remove a notification. */
  notification: (notificationId: string) => string;
  /** GET / POST: saved filters. */
  savedFilters: () => string;
  /** PUT / DELETE: one saved filter. */
  savedFilter: (savedFilterId: string) => string;
}

/**
 * Judge Chambers endpoints
 * @param versionedUrl The API base URL, ending in a slash.
 */
export const judgeChambersEndpoints = (
  versionedUrl: string,
): IJudgeChambersEndpoints => {
  const baseUrl = `${versionedUrl}judge-chambers`;
  const clerkReviewUrl = `${baseUrl}/clerk-review`;

  return {
    summary: () => baseUrl,
    tags: (chamberId: string) => `${baseUrl}/${chamberId}/tags`,
    tag: (chamberId: string, tagId: string) =>
      `${baseUrl}/${chamberId}/tags/${tagId}`,
    documentTag: (chamberId: string, documentId: number, tagId: string) =>
      `${baseUrl}/${chamberId}/documents/${documentId}/tags/${tagId}`,
    filingDocumentTags: (filingUniqueId: string) =>
      `${baseUrl}/filings/${filingUniqueId}/document-tags`,
    documentNotes: (chamberId: string, documentId: number) =>
      `${baseUrl}/${chamberId}/documents/${documentId}/notes`,
    caseNotes: (chamberId: string, caseId: number) =>
      `${baseUrl}/${chamberId}/cases/${caseId}/notes`,
    note: (chamberId: string, noteId: string) =>
      `${baseUrl}/${chamberId}/notes/${noteId}`,
    filingNotes: (filingUniqueId: string) =>
      `${baseUrl}/filings/${filingUniqueId}/notes`,
    noteReplies: (chamberId: string, noteId: string) =>
      `${baseUrl}/${chamberId}/notes/${noteId}/replies`,
    mentionCandidates: (chamberId: string) =>
      `${baseUrl}/${chamberId}/mention-candidates`,
    clerkReviewMentionCandidates: (documentId: number) =>
      `${clerkReviewUrl}/documents/${documentId}/mention-candidates`,
    clerkReviewDocumentNotesQuery: () =>
      `${clerkReviewUrl}/document-notes/query`,
    clerkReviewDocumentNotes: (documentId: number) =>
      `${clerkReviewUrl}/documents/${documentId}/notes`,
    clerkReviewNote: (noteId: string) => `${clerkReviewUrl}/notes/${noteId}`,
    clerkReviewNoteReplies: (noteId: string) =>
      `${clerkReviewUrl}/notes/${noteId}/replies`,
    documentAssignee: (chamberId: string, documentId: number) =>
      `${baseUrl}/${chamberId}/documents/${documentId}/assignee`,
    filingAssignments: (filingUniqueId: string) =>
      `${baseUrl}/filings/${filingUniqueId}/assignments`,
    notifications: () => `${baseUrl}/notifications`,
    notificationRead: (notificationId: string) =>
      `${baseUrl}/notifications/${notificationId}/read`,
    notificationsRead: () => `${baseUrl}/notifications/read`,
    notification: (notificationId: string) =>
      `${baseUrl}/notifications/${notificationId}`,
    savedFilters: () => `${baseUrl}/saved-filters`,
    savedFilter: (savedFilterId: string) =>
      `${baseUrl}/saved-filters/${savedFilterId}`,
  };
};
